import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Real connectivity detection hook.
 * Uses navigator.onLine as a fast signal, then verifies with an actual
 * network probe (tiny HEAD request to a known-available origin).
 * Re-checks periodically and on window online/offline events.
 */
const PROBE_URLS = [
  'https://dns.google/generate_204',
  'https://cloudflare.com/cdn-cgi/trace',
];
const PROBE_INTERVAL = 8000;       // check every 8s when offline
const PROBE_INTERVAL_ONLINE = 30000; // check every 30s when online
const PROBE_TIMEOUT = 5000;         // 5s timeout per probe

const fetchProbe = (url, signal) =>
  fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal });

const probeNetwork = async () => {
  for (const url of PROBE_URLS) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT);
      await fetchProbe(url, controller.signal);
      clearTimeout(timeoutId);
      return true;
    } catch {
      // Try next URL
    }
  }
  return false;
};

const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    const result = await probeNetwork();
    setIsOnline(result);
    return result;
  }, []);

  useEffect(() => {
    // Initial probe
    check();

    const handleOnline = () => {
      // Browser says we're online — verify with probe
      check();
    };

    const handleOffline = () => {
      // Browser says offline — verify with probe instead of trusting blindly
      check();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Periodic probe — faster when offline so we recover quickly
    const startInterval = () => {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(async () => {
        const result = await probeNetwork();
        setIsOnline(prev => {
          // Adjust interval when state changes
          if (prev !== result) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
              probeNetwork().then(setIsOnline);
            }, result ? PROBE_INTERVAL_ONLINE : PROBE_INTERVAL);
          }
          return result;
        });
      }, isOnline ? PROBE_INTERVAL_ONLINE : PROBE_INTERVAL);
    };
    startInterval();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return isOnline;
};

export default useNetworkStatus;
